/**
 * Derivacoes CLIENT-SIDE da aba Processos sobre as respostas do `/api/v1/orquestrador`.
 *
 * O BFF ja' entrega o macro pronto (`/esteira`: linhas por cadeia de grupos, dedup
 * feita no servidor) e o board de 1 grupo (`/grupos/{id}/board`: contagens por bot).
 * O que fica no front e' so' o que depende de ESTADO DE TELA: encadear bots em linhas
 * visuais pelo `dep_bot`, e montar as listas de empresa dos popups/card Fila a partir
 * do `/resumo` (que ja' traz, por empresa, o status em cada bot).
 */

import type { IBotDaEmpresa, IEmpresaResumo, IModuloNano, StatusEsteira } from '../api';
import { ORDEM_STATUS } from './ordemStatus';

/** Item minimo que os popups e o card Fila listam. */
export interface IEmpresaComStatus {
  codiEmp: string;
  nome: string;
  st: StatusEsteira;
  /** Motivo coerente com `st`: motivo_aviso quando warn, motivo_erro quando err. */
  motivo: string | null;
  /** Em quantos bots DIFERENTES do grupo a empresa aparece (so' em lista de grupo). */
  qtdBots?: number;
  /**
   * Quantos bots do grupo CONCLUIRAM e quantos FALHARAM (25/09).
   *
   * A linha mostra o status VENCEDOR da dedup (`done` vence `err`, §3.3), e uma
   * empresa que passou em 8 bots e falhou em 1 aparecia so' como "Concluído"
   * com uma caixa vermelha embaixo — o dono olhou e ficou confuso, porque a
   * linha nao dizia de onde vinha a contradicao.
   *
   * "passou em 8 · falhou em 1" e' o resumo que reconcilia as duas coisas na
   * propria linha, sem mudar nenhum numero.
   */
  botsOk?: number;
  botsFalha?: number;
  /**
   * Quantas EXECUCOES falharam no periodo, mesmo que a empresa tenha concluido
   * depois. E' HISTORICO, nunca status.
   *
   * Existe porque o badge da linha mostrava `qtdBots` — "em quantos bots ela
   * aparece" — e todo mundo lia como "quantas vezes falhou". Sao grandezas
   * diferentes: a empresa 1083 tinha badge "(4)" com 2 falhas no banco.
   */
  falhas?: number | null;
  /**
   * Por que a empresa esta' PENDENTE (28/09): o texto do ultimo skip do par.
   *
   * NAO e' `motivo` de proposito. `motivo` sai de `motivo_erro`, e medido em
   * producao 251 dos 1276 pares em skip chegam com `motivo_erro` preenchido por
   * uma falha ANTIGA — a caixa da pendente mostraria o erro velho como se fosse
   * a razao da pendencia.
   */
  motivoSkip?: string;
  /**
   * Data e texto da ULTIMA falha do par na janela — o historico que `falhas` so'
   * conta. Os dois acompanham o mesmo bot de `falhas`.
   *
   * `ultimaFalhaEm` e' horario de Sao Paulo sem fuso: ler com `lerDataFalha`.
   * `ultimaFalhaMotivo` so' pode ser exibido dentro da caixa da PENDENTE — em
   * linha concluida entra so' a data (regra do dono, 25/09 e 28/09).
   *
   * Ausentes (`undefined`) quando o backend nao manda: BFF antigo ou resposta em
   * cache.
   */
  ultimaFalhaEm?: string;
  ultimaFalhaMotivo?: string;
  /**
   * As DUAS mensagens da entrada, preservadas lado a lado (24/09).
   *
   * `motivo` guarda só a coerente com o status (`motivoDaEntrada`): a empresa em
   * `warn` mostrava o aviso e a em `err` mostrava o erro, e a outra sumia. Na
   * MACRO, onde erro e alerta viram "Falha de processamento", esconder uma das
   * duas deixaria a caixa contando metade da história — medido em 24/09: 143
   * pares (empresa, bot) têm erro E alerta ao mesmo tempo.
   *
   * Os dois campos convivem porque respondem a telas diferentes: board e nano
   * seguem mostrando só a do status (a distinção importa lá), a macro mostra as
   * duas.
   */
  motivoErro?: string | null;
  motivoAviso?: string | null;
  /**
   * Nome REAL do bot de onde o status/motivo vieram (nunca o apelido de exibicao)
   * — e' o que a ponte popup → aba Erros usa para filtrar a auditoria. Em lista de
   * grupo e' o bot VENCEDOR da dedup (o que definiu st/motivo). Ausente nas listas
   * que nao nascem do /resumo (fila da execucao).
   */
  bot?: string;
  /**
   * Ultima atividade da empresa no /resumo (ISO) — APROXIMACAO do instante do
   * erro/aviso exibido, NAO o carimbo dele: `IBotDaEmpresa` nao tem timestamp, e
   * este campo e' a ultima atividade da empresa em QUALQUER bot. A ponte para a
   * auditoria manda so' como `foco_ts` (destaque com match tolerante), nunca como
   * filtro de periodo.
   */
  ts?: string | null;
  /**
   * Ha erro/aviso em ALGUM bot do grupo, mesmo quando `st` (vencedor da dedup) e'
   * outro — ex.: concluiu no bot-b e falhou no bot-a, e ORDEM_STATUS faz `done`
   * vencer. So' aparece em lista de GRUPO, e serve unicamente para manter o atalho
   * "ver na auditoria" disponivel: o texto/estado visual do popup segue o vencedor.
   * O atalho entao filtra por empresa + status, SEM bot (o popup de grupo nao
   * inventa bot — filtrar por um arbitrario esconderia a ocorrencia).
   */
  ocorrenciaEmOutroBot?: 'err' | 'warn';
  /**
   * MENSAGEM da ocorrencia registrada em `ocorrenciaEmOutroBot` (motivo_erro ou
   * motivo_aviso do bot que a dedup NAO elegeu). Sem isso a linha do popup de
   * grupo ficava muda: o atalho existia, mas o texto do erro/aviso morria na
   * dedup — "clico no no' de grupo e nao aparecem mais os warnings" (12/09).
   * Acompanha sempre o mesmo bot/status de `ocorrenciaEmOutroBot`; NUNCA
   * substitui `motivo` (que segue o vencedor e manda no estado visual da linha).
   */
  motivoEmOutroBot?: string | null;
  /**
   * DE QUAL BOT veio a ocorrencia acima (22/09). O dado existia no laco da dedup
   * e era descartado: a caixa do popup dizia "Mensagem de erro" sem identificar a
   * origem, e o atalho para a auditoria navegava com `bot: null` porque o front
   * nao sabia mais de onde vinha. Num grupo com 4 bots, saber QUAL deles quebrou
   * e' a primeira pergunta de quem abre o popup.
   */
  botDaOcorrencia?: string | null;
  /**
   * Empresa presente na EXECUCAO mas ausente do /resumo (keep-rule de
   * `filtrarPorRecorte`, ex.: inativa/fora de vigencia presente na execucao) — o
   * popup exibe a tag discreta "fora do resumo". Marcada por `marcarForaDoResumo`
   * (derivarNano), nunca vem do BFF.
   */
  foraDoResumo?: boolean;
}

/** Nome exibivel da empresa — cadastro primeiro, codi_emp como ultimo recurso. */
export const nomeEmpresa = (e: IEmpresaResumo): string =>
  e.empresa?.nome || e.empresa?.razao_social || e.codi_emp;

/** Nome exibivel do modulo nano — o catalogo pode vir sem nome (fallback pelo id). */
export const nomeModulo = (m: Pick<IModuloNano, 'modulo_id' | 'nome'>): string =>
  m.nome || `Módulo ${m.modulo_id}`;

/**
 * Encadeia bots em linhas visuais pela dependencia `dep_bot` — mesmo algoritmo das
 * cadeias do prototipo: raizes = bots sem dependencia EFETIVA (null ou apontando para
 * fora do conjunto exibido); cada linha segue quem depende do cursor.
 *
 * Bot que sobrar (ciclo preexistente no dado) vira linha propria: melhor aparecer
 * fora de ordem do que sumir da tela.
 */
export const montarLinhasBots = <T extends { bot: string; dep_bot: string | null }>(
  bots: T[],
): T[][] => {
  const nomes = new Set(bots.map(b => b.bot));
  const usados = new Set<string>();
  const linhas: T[][] = [];

  const proximoDe = (cursor: string): T | undefined =>
    bots.find(b => b.dep_bot === cursor && !usados.has(b.bot));

  const raizes = bots.filter(b => !b.dep_bot || !nomes.has(b.dep_bot));
  raizes.forEach(raiz => {
    if (usados.has(raiz.bot)) return;
    const linha: T[] = [raiz];
    usados.add(raiz.bot);
    let proximo = proximoDe(raiz.bot);
    while (proximo) {
      linha.push(proximo);
      usados.add(proximo.bot);
      proximo = proximoDe(proximo.bot);
    }
    linhas.push(linha);
  });

  bots
    .filter(b => !usados.has(b.bot))
    .forEach(orfao => {
      usados.add(orfao.bot);
      linhas.push([orfao]);
    });

  return linhas;
};

/**
 * Motivo coerente com o status exibido: a caixa ambar do popup mostra o
 * motivo_aviso quando o status e' warn, simetrica a caixa vermelha de erro
 * (pedido 04/09: "traga o log de warning, assim como o do Error").
 */
export const motivoDaEntrada = (entrada: IBotDaEmpresa): string | null => {
  if (entrada.status === 'warn') return entrada.motivo_aviso;
  if (entrada.status !== 'err') return entrada.motivo_erro;
  // ERRO SEM `motivo_erro` (14/10, achado do dono no Auditor Notas Fiscais: a
  // linha dizia "sem mensagem" e o histórico da mesma empresa mostrava o texto).
  // Dois caminhos levam ao `err` sem passar por `motivo_erro`: o skip de
  // BLOQUEIO (o texto mora em `motivo_skip`, ex. "certificado não cadastrado") e
  // a falha operacional da unidade (o texto mora em `ultima_falha_motivo`). O
  // `motivo_erro` continua vencendo quando existe — o fallback só preenche o vazio.
  return entrada.motivo_erro || entrada.motivo_skip || entrada.ultima_falha_motivo || null;
};

/** Todas as empresas que tem entrada no bot, com o status DELA naquele bot. */
export const empresasDoBotComStatus = (
  resumo: IEmpresaResumo[],
  bot: string,
): IEmpresaComStatus[] =>
  resumo.flatMap(e => {
    const entrada = e.bots.find((b: IBotDaEmpresa) => b.bot === bot);
    if (!entrada) return [];
    return [
      {
        codiEmp: e.codi_emp,
        nome: nomeEmpresa(e),
        st: entrada.status,
        motivo: motivoDaEntrada(entrada),
        bot,
        ts: e.ultima_atividade,
        falhas: entrada.falhas_no_periodo ?? null,
        motivoErro: entrada.motivo_erro ?? null,
        motivoAviso: entrada.motivo_aviso ?? null,
        // `|| undefined`, nao `?? null`: ausente, null e string vazia sao o mesmo
        // "nao ha' o dado", e a propriedade fica undefined quando o backend nao
        // manda.
        motivoSkip: entrada.motivo_skip || undefined,
        ultimaFalhaEm: entrada.ultima_falha_em || undefined,
        ultimaFalhaMotivo: entrada.ultima_falha_motivo || undefined,
      },
    ];
  });

/**
 * Empresas de um GRUPO (uniao dos bots), deduplicadas por empresa: repetida conta
 * 1 vez com o status de MENOR indice em ORDEM_STATUS (done vence err, que vence
 * warn...), e `qtdBots` diz em quantos bots diferentes ela apareceu.
 *
 * A mesma dedup existe no `/esteira` do BFF (contrato §3.3) — aqui ela e' refeita
 * para as LISTAS (popup e card Fila), que o endpoint agregado nao devolve. Divergir
 * daqui e' regressao do bug historico do prototipo (no' e popup com contas diferentes).
 *
 * `baseDoBot` (regime por nivel, 08/09): recorte do /resumo POR BOT aplicado ANTES
 * da dedup — e' por onde o filtro de regime efetivo da cadeia entra na fonte (o
 * regime pode variar por bot dentro do mesmo grupo: bot.regime vence o do grupo).
 * Ausente = sem recorte extra (comportamento de sempre).
 *
 * A precedencia de status NAO muda (mexer nela mudaria a semantica do no'/popup):
 * o que a revisao de 09/09 acrescentou e' so' `ocorrenciaEmOutroBot`, que registra
 * que existe err/warn em algum bot mesmo quando o vencedor e' outro status — sem
 * isso, a empresa que concluiu no bot-b e falhou no bot-a perdia o atalho para a
 * auditoria justo no caminho mais provavel de investigacao.
 */
export const empresasDoGrupoComStatus = (
  resumo: IEmpresaResumo[],
  botsDoGrupo: string[],
  // eslint-disable-next-line no-unused-vars
  baseDoBot: (bot: string) => IEmpresaResumo[] = () => resumo,
): IEmpresaComStatus[] => {
  const porEmpresa = new Map<string, IEmpresaComStatus>();
  // Pior ocorrencia (err > warn) vista em QUALQUER bot, por empresa — independente
  // de quem venceu a dedup. So' alimenta o atalho para a auditoria.
  const ocorrencias = new Map<string, 'err' | 'warn'>();
  // MENSAGEM da ocorrencia acima — mesma chave, mesma precedencia (err vence warn),
  // para a linha do popup poder exibir o texto do erro/aviso que a dedup nao elegeu.
  const motivosOcorrencia = new Map<string, string | null>();
  // E de QUAL BOT ela veio (22/09). O dado existia no laco e era descartado: a
  // caixa dizia "Mensagem de erro" sem identificar a origem, e o atalho para a
  // auditoria ia com `bot: null` porque o front nao sabia mais de onde vinha.
  const botsOcorrencia = new Map<string, string | null>();
  const anotarOcorrencia = (
    codiEmp: string,
    st: StatusEsteira,
    motivo: string | null,
    bot: string | null,
  ) => {
    if (st !== 'err' && st !== 'warn') return;
    // `err` sobrescreve um `warn` ja' anotado; o primeiro `warn` vence os demais.
    if (st === 'err' || !ocorrencias.has(codiEmp)) {
      ocorrencias.set(codiEmp, st);
      motivosOcorrencia.set(codiEmp, motivo);
      botsOcorrencia.set(codiEmp, bot);
    }
  };
  botsDoGrupo.forEach(bot => {
    empresasDoBotComStatus(baseDoBot(bot), bot).forEach(item => {
      anotarOcorrencia(item.codiEmp, item.st, item.motivo, item.bot ?? null);
      const atual = porEmpresa.get(item.codiEmp);
      if (!atual) {
        porEmpresa.set(item.codiEmp, {
          ...item,
          qtdBots: 1,
          botsOk: item.st === 'done' || item.st === 'warn' ? 1 : 0,
          botsFalha: item.st === 'err' ? 1 : 0,
        });
        return;
      }
      atual.qtdBots = (atual.qtdBots ?? 1) + 1;
      // Contados ANTES da disputa de status: o resumo "passou em N · falhou em
      // M" descreve o GRUPO inteiro, nao o bot que venceu a dedup.
      if (item.st === 'done' || item.st === 'warn') atual.botsOk = (atual.botsOk ?? 0) + 1;
      if (item.st === 'err') atual.botsFalha = (atual.botsFalha ?? 0) + 1;
      // `falhas` acompanha o BOT VENCEDOR, nao soma o grupo (24/09, corrigido).
      //
      // A soma parecia certa ("quantas vezes a empresa falhou neste grupo") e
      // produziu um numero que a tela nao consegue explicar: a empresa 1083
      // apareceu com badge "Falha (83)" — 2 falhas no BOT_EFD_REINF + 73 no
      // TRANSMISSAO_EFD_CONTRIBUICOES + 7 + 1 — ao lado do nome de UM bot so'.
      // Quem le' associa o 83 ao bot escrito ali, e nenhum deles tem 83.
      //
      // O badge fica na mesma linha que nomeia o bot da mensagem, entao ele tem
      // que contar o MESMO bot. A troca acontece junto de `st`/`motivo`/`bot`,
      // logo abaixo — aqui so' nao se soma.
      // As duas mensagens sobrevivem a dedup mesmo quando o BOT perde: na macro
      // a caixa mostra erro E alerta, e eles podem ter vindo de bots diferentes
      // do mesmo grupo. `??=` preserva o primeiro que chegou — nao sobrescreve.
      atual.motivoErro = atual.motivoErro ?? item.motivoErro ?? null;
      atual.motivoAviso = atual.motivoAviso ?? item.motivoAviso ?? null;
      if ((ORDEM_STATUS[item.st] ?? 9) < (ORDEM_STATUS[atual.st] ?? 9)) {
        atual.st = item.st;
        atual.motivo = item.motivo;
        // `falhas` acompanha o vencedor: o badge e o nome do bot na mesma linha
        // precisam falar do MESMO bot.
        atual.falhas = item.falhas ?? null;
        // Data e texto da ultima falha trocam JUNTO de `falhas` (28/09): se a
        // data viesse de outro bot a caixa diria "falhou 2x" de um bot com a
        // data de outro. O motivo do skip idem — e' o do bot que a linha nomeia.
        atual.motivoSkip = item.motivoSkip;
        atual.ultimaFalhaEm = item.ultimaFalhaEm;
        atual.ultimaFalhaMotivo = item.ultimaFalhaMotivo;
        // `bot` acompanha st/motivo: e' o bot que EXPLICA a mensagem exibida —
        // divergir aqui mandaria a auditoria filtrar pelo bot errado.
        atual.bot = item.bot;
        atual.ts = item.ts;
      }
    });
  });
  return Array.from(porEmpresa.values()).map(linha => {
    const ocorrencia = ocorrencias.get(linha.codiEmp);
    // So' marca quando o VENCEDOR nao e' a propria ocorrencia — se st ja' e' err/warn
    // o atalho normal (por bot) ja' existe e nada muda.
    if (!ocorrencia || linha.st === 'err' || linha.st === 'warn') return linha;
    return {
      ...linha,
      ocorrenciaEmOutroBot: ocorrencia,
      motivoEmOutroBot: motivosOcorrencia.get(linha.codiEmp) ?? null,
      botDaOcorrencia: botsOcorrencia.get(linha.codiEmp) ?? null,
    };
  });
};
